'use client';

import styled from 'styled-components';

import { MenuItem } from '../../../atoms/menu-item/MenuItem';

type Position = {
	x: number;
	y: number;
};

export namespace S {
	export const BaseSelectContainer = styled.div<{ $disabled?: boolean; $isReadonly?: boolean }>`
		position: relative;
		pointer-events: ${({ $disabled, $isReadonly }) => ($disabled || $isReadonly ? 'none' : '')};
	`;

	export const BaseSelect = styled.div`
		cursor: pointer;
	`;

	export const InputWrapper = styled.div<{ $disabled?: boolean; $hasError?: boolean }>`
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		background: ${({ theme }) => theme.colors.backgrounds.main.input};
		${({ theme }) => theme.border.radius.medium};
		min-height: ${({ theme }) => theme.sizing.xlarge};
		height: 56px;
		padding: 0 ${({ theme }) => theme.spacings.xsmall};

		border: ${({ theme }) => theme.border.width.medium} solid
			${({ $hasError, theme }) => ($hasError ? theme.colors.borders.error.enabled : 'transparent')};

		&:disabled {
			background-color: unset;
			border-color: unset;
		}
	`;

	export const InputContent = styled.div<{ $label: boolean }>`
		flex: 1;
		position: relative;
		align-self: stretch;
		margin: ${({ $label, theme }) =>
			$label ? `${theme.spacings.large} auto ${theme.spacings.xxxsmall} auto` : '0 auto'};
	`;

	export const FloatingLabel = styled.label<{ $disabled?: boolean }>`
		top: -5px;
		display: block;
		position: absolute;
		transform-origin: left top;
		transform: translateY(-10px);
		${({ theme }) => theme.fonts.text.caption1};
		color: ${({ $disabled, theme }) =>
			$disabled ? theme.colors.text.main.disabled : theme.colors.text.main.subtle};
	`;

	export const BaseSelectedOption = styled.span<{ $disabled?: boolean; $isPlaceholder: boolean }>`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ $disabled, $isPlaceholder, theme }) =>
			$disabled
				? theme.colors.text.main.disabled
				: $isPlaceholder
					? theme.colors.text.main.subtle
					: theme.colors.text.main.enabled};
		display: flex;
		align-items: center;
		position: relative;
		height: 100%;
	`;

	export const Dropdown = styled.ul<{ $minWidth: number; $opacity: number; $position: Position }>`
		opacity: 0;
		position: absolute;
		overflow-y: scroll;
		opacity: ${({ $opacity }) => $opacity};
		top: ${({ $position }) => $position.y}px;
		left: ${({ $position }) => $position.x}px;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.xsmall} 0;
		margin: 0;
		${({ theme }) => theme.border.radius.medium}
		${({ theme }) => theme.elevations.level1}
		z-index: 11;
		width: fit-content;
		min-width: ${({ $minWidth }) => $minWidth}px;
		max-width: 500px;
		max-height: 254px;
	`;

	export const BaseSelectListItem = styled.li`
		${({ theme }) => theme.fonts.text.text3};

		&:hover {
			cursor: pointer;
			color: ${({ theme }) => theme.colors.text.onDark.enabled};
			background-color: ${({ theme }) => theme.colors.text.main.enabled};
		}
	`;

	export const ItemWrapper = styled(MenuItem)`
		padding: ${({ theme }) => theme.spacings.medium};
	`;

	export const EmptyListWrapper = styled.div<{ $minWidth: number; $position: Position }>`
		position: absolute;
		overflow-y: scroll;
		top: ${({ $position }) => $position.y}px;
		left: ${({ $position }) => $position.x}px;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.xsmall};
		margin: 0;
		${({ theme }) => theme.border.radius.medium}
		${({ theme }) => theme.elevations.level1}
		z-index: 11;
		width: fit-content;
		min-width: ${({ $minWidth }) => $minWidth}px;
		max-width: 500px;
		max-height: 254px;
	`;

	export const EmptyText = styled.p`
		${({ theme }) => theme.fonts.text.text2};
	`;

	export const BottomSheetContainer = styled.div<{ $extraPadding?: boolean }>`
		padding: ${({ $extraPadding, theme }) =>
			$extraPadding ? `${theme.spacings.small} ${theme.spacings.large}` : 0};
	`;

	export const BottomSheetTitle = styled.p`
		${({ theme }) => theme.fonts.headings.title6};
	`;

	export const BaseSelectListMobile = styled.div`
		overflow-y: scroll;
		height: 300px;
		margin-top: ${({ theme }) => theme.spacings.small};
		list-style-type: none;
	`;
	export const BottomWrapper = styled.div<{ $isRTL: boolean }>`
		display: flex;
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const SupportingInputInfo = styled.span<{ $disabled?: boolean; $hasError: boolean }>`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ $disabled, $hasError, theme }) => {
			if ($disabled) return theme.colors.text.main.disabled;
			if ($hasError) return theme.colors.borders.error.enabled;

			return theme.colors.text.main.enabled;
		}};
	`;

	export const SupportingText = styled(SupportingInputInfo)`
		align-self: flex-start;
		flex-grow: 2;
	`;
}
